<template>
  <div class="mt-1 mb-5 mb-md-4 position-relative">
    <div v-if="!showingDiscounts" id="container" class="strorefront-disabled-mask">
      <div v-if="isPackToOrder" class="mb-3">
        <h6 class="text-light font-weight-normal mb-3 text-left text-uppercase text-center">
          <span style="letter-spacing: 1px"> Please Use Calculator To Order </span>
        </h6>
        <BushelBtn class="btn-block" variant="success" @click="$emit('launchPTOModal', { product: product, batch: batch })">
          <i class="fas fa-calculator" />
        </BushelBtn>
      </div>
      <div v-else class="checkmark-overlay" :class="productAdded && activeLocation ? '' : 'hidden'">
        <Checkmark :title="'Success'" :subtext="'Added To Your Cart'" :play-state="productAdded" />
      </div>
      <div :class="!isPackToOrder ? 'checkmark-underlay' : ''">
        <div v-if="!isPackToOrder">
          <div class="row mb-2">
            <div class="col-4">
              <div class="d-table h-80 w-100" style="table-layout: fixed">
                <label class="d-table-cell align-middle text-right text-lg-left text-lightest font-weight-normal" for="store_item_quantity">
                  <div class="mt--half">Quantity</div>
                  <div v-if="batch.back_order" class="mt--half">
                    <span class="badge badge-pill badge-success text-white text-uppercase text-smallest font-weight-normal pt-1"> Preorder </span>
                  </div>
                </label>
              </div>
            </div>
            <div class="col-8">
              <BushelTextInput
                v-model="productToAddToStore.quantity"
                :error="errorInInputField"
                :name="'store_item_quantity'"
                :label-active="'Quantity'"
                hide-label
                :input-id="'store_item_quantity'"
                :suffix="getUnit(product, true).abbreviation"
                type="number"
                :maxlength="10"
                :number-type-allow-decimal="categoryTypeCanHaveDecimal(product.category.name, product)"
                :number-type-max-precision="6"
                @input-blurred="quantityEnteredCheck"
              />
              <span v-if="quantityInCart()">({{ quantityInCart() }} already in <i class="fa fa-shopping-cart" />)</span>
              <p v-if="sampleInCart()" class="mb-0">(Sample in <i class="fa fa-shopping-cart" />)</p>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-4">
              <div class="d-table h-100 w-100" style="table-layout: fixed">
                <div class="d-table-cell align-middle">
                  <h6 class="text-lightest mb-2 text-right text-lg-left font-weight-normal">Total</h6>
                </div>
              </div>
            </div>
            <div class="col-8">
              <h6 class="text-medium font-weight-bold text-right">
                {{ currency(totalPrice) }}
              </h6>
            </div>
          </div>
          <div class="row mb-4 mb-md-3">
            <div class="col-12">
              <BushelBtn
                :variant="productAdded ? 'success' : 'success'"
                class="btn-lg btn-block pb-2"
                :disabled="quantitySubmitDisabled"
                @click="addToCartTriggered"
              >
                <span v-if="!productAdded">Add to Cart</span>
                <span v-else>Product Added</span>
              </BushelBtn>
            </div>
          </div>
        </div>
        <div class="row">
          <div
            v-if="bulkDiscounts.length > 0"
            class="mb-3"
            :class="{
              'col-12': !product.allow_samples || (product.track_sample_quantity && product.sample_quantity <= 0),
              'col-6 col-sm-4 col-md-12 col-xl-5 pr-xl-0': product.allow_samples && (!product.track_sample_quantity || product.sample_quantity >= 0),
            }"
          >
            <BushelBtn variant="primary" class="btn-sm btn-block text-small" @click="showingDiscounts = !showingDiscounts"> Discounts </BushelBtn>
          </div>
          <div
            v-if="batch.samples_available"
            class="mb-3"
            :class="{ 'col-12': bulkDiscounts.length <= 0, 'col-6 col-sm-8 col-md-12 col-xl-7': bulkDiscounts.length > 0 }"
          >
            <BushelBtn
              :variant="sampleRequested ? 'success' : 'primary'"
              class="btn-sm btn-block text-small"
              :disabled="sampleRequested"
              @click="sampleRequestTriggered"
            >
              <span v-if="!sampleRequested">Request Sample</span>
              <span v-else>Sample Requested</span>
            </BushelBtn>
          </div>
        </div>
      </div>
    </div>

    <div v-else>
      <BushelCard v-for="discount in bulkDiscounts" :key="discount.id" class="p-2" body-class="p-2">
        <h5 class="mb-0 text-medium font-weight-bold">
          {{
            ` $${discount.discount / 100} discount on ${discount.minimum_quantity} ${discount.unit_of_measurement.name}s of ${
              discount.product_category.name
            } ${discount.product_type_id ? " - " : ""} ${discount.product_type_id ? discount.product_type.name : ""}`
          }}
        </h5>
      </BushelCard>
      <BushelBtn
        v-if="bulkDiscounts.length > 0"
        variant="primary"
        ripple-color="primary"
        class="btn-sm btn-block text-small"
        @click="showingDiscounts = !showingDiscounts"
      >
        {{ `${"Hide"} ${"Hide"}` }}
      </BushelBtn>
    </div>
  </div>
</template>

<script>
import { categoryTypeCanHaveDecimal } from "@/services/inventory/inventory";
import { showBatchDocumentsUploadFieldCheck } from "@/services/inventory/showBatchFieldChecks";
import { getUnit } from "@/services/storefront/storefront";
import Checkmark from "@/components/common/Checkmark.vue";
import { currency } from "@/services/filters";

export default {
  components: { Checkmark },
  props: {
    product: { type: Object, required: true },
    batch: { type: Object, required: true },
    bulkDiscounts: { type: Array, required: true },
    cartItems: { type: Array },
    activeLocation: { type: Object },
  },
  emits: ["addedProduct", "launchPTOModal"],
  data() {
    return {
      productToAddToStore: {
        quantity: null,
        id: this.product.id,
      },
      sampleRequested: false,
      productAdded: false,
      showingDiscounts: false,
      inputValue: null,
      maxQuantity: null,
      showBatchDocumentsUploadField: showBatchDocumentsUploadFieldCheck(this.product.category.name),
    };
  },
  computed: {
    errorInInputField() {
      if (this.productToAddToStore.quantity && Number.isNaN(Number(this.productToAddToStore.quantity))) {
        return true;
      }
      if (
        this.productToAddToStore.quantity &&
        this.productToAddToStore.quantity % 1 != 0 &&
        !this.categoryTypeCanHaveDecimal(this.product.category.name, this.product)
      ) {
        return true;
      }
      const quantityInCart = this.quantityInCart();
      if (!this.batch.disable_inventory_tracking && Number(quantityInCart) + Number(this.productToAddToStore.quantity) > this.batch.quantity) {
        return true;
      }
      return false;
    },
    quantitySubmitDisabled() {
      if (!this.productToAddToStore || !this.productToAddToStore.quantity) {
        return true;
      }
      if (this.errorInInputField) {
        return true;
      }
      if (this.productAdded || !this.batch.thc_limit) {
        return true;
      }

      const quantityInCart = this.quantityInCart();
      if (!this.batch.disable_inventory_tracking && Number(quantityInCart) + Number(this.productToAddToStore.quantity) > this.batch.quantity) {
        return true;
      }

      return false;
    },
    itemPrice() {
      return this.batch.tiered_listing_price || this.batch.listing_price;
    },
    totalPrice() {
      return this.productToAddToStore.quantity * this.itemPrice;
    },
    isPackToOrder() {
      return this.product.category.name == "Pack To Order";
    },
  },
  watch: {
    batch(oldVal, newVal) {
      this.productToAddToStore.quantity = null;
    },
  },
  methods: {
    categoryTypeCanHaveDecimal,
    getUnit,
    currency,
    sampleInCart() {
      if (this.cartItems && this.cartItems.length) {
        const foundItem = this.cartItems.find((item) => item.batch_id == this.batch.id && item.sample);
        if (foundItem) {
          return `${foundItem.quantity.toString()}`;
        }
      }
      return 0;
    },

    quantityInCart() {
      if (this.cartItems && this.cartItems.length) {
        const foundItem = this.cartItems.find((item) => item.batch_id == this.batch.id && !item.sample);
        if (foundItem) {
          return `${foundItem.quantity.toString()}`;
        }
      }
      return 0;
    },

    resetProductStoreForm() {
      this.productToAddToStore.quantity = null;
      setTimeout(() => {
        this.productAdded = false;
      }, 1500);
    },
    quantityEnteredCheck() {
      let newQuantity = Number(this.productToAddToStore.quantity);

      if (newQuantity + this.quantityInCart() > this.batch.quantity && !this.batch.disable_inventory_tracking) {
        newQuantity = this.batch.quantity - this.quantityInCart();
      }
      if (Number.isNaN(Number(newQuantity))) {
        newQuantity = 0;
      }
      if (newQuantity && newQuantity % 1 != 0 && !this.categoryTypeCanHaveDecimal(this.product.category_name, this.product)) {
        newQuantity = parseInt(newQuantity, 10);
      }

      this.productToAddToStore.quantity = newQuantity;
    },
    addToCartTriggered() {
      if (this.productToAddToStore.quantity) {
        this.productAdded = true;
        this.$emit("addedProduct", {
          batch_id: this.batch.id,
          company_id: this.productToAddToStore.company_id,
          quantity: this.productToAddToStore.quantity,
          sample: false,
          product_id: this.productToAddToStore.id,
        });
        this.resetProductStoreForm();
      }
    },
    sampleRequestTriggered() {
      this.sampleRequested = true;
      // dont really need sample_size, will look it up in php
      this.$emit("addedProduct", {
        batch_id: this.batch.id,
        quantity: this.batch.sample_size,
        sample: true,
        company_id: this.productToAddToStore.company_id,
        product_id: this.productToAddToStore.id,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
#container {
  display: grid;
  grid-template-rows: 1fr;

  .checkmark-overlay {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background: white;
    z-index: 100;
    transition:
      opacity 500ms,
      visibility 0ms;
    grid-row-start: 1;
    grid-column-start: 1;

    &.hidden {
      opacity: 0;
      visibility: hidden;
      transition-delay: 0ms, 500ms;
    }
  }

  .checkmark-underlay {
    grid-row-start: 1;
    grid-column-start: 1;
  }
}
</style>
